import { defineComponent } from "vue";
import { mapState } from "pinia";
import { Container } from "aurelia-dependency-injection";

import { useConvertToPendingPal } from "../../../store/useConvertToPendingPal";
import { IEnumResponse } from "../../../../../../resources-vue/vue-interfaces/i-enum";
import { DeviceStatuses } from "../../../../../../enums/ReferralEnums/DeviceStatusEnum";
import { IGetImplantableDeviceQuery, PatientService } from "../../../../../../services/patient.service";
import { ToastrService } from "../../../../../../services/toastr.service";
import { IPageCallbackResult } from "../../../../../../resources-vue/vue-interfaces/i-page-container";
import { PAGE_FAIL_RESPONSE } from "../../../../../CentralIntake/Inquiry/CentralInquiry";
import ImplantableDeviceRow from "../ImplantableDeviceRow/ImplantableDeviceRow.vue";
import { ImplantableDevice } from "../../../../../../models/pal-health-history/implantable-device";

import ErrorMessageHandler from "../../../../../../resources-vue/vue-custom-elements/ErrorMessageHandler/ErrorMessageHandler.vue";
import FormSection from "../../../../../../resources-vue/vue-custom-elements/FormSection/FormSection.vue";
import Pagination from "../../../../../../resources-vue/vue-elements/Pagination/Pagination.vue";
import SortableTh from "../../../../../../resources-vue/vue-custom-elements/SortableTh/SortableTh.vue";
import ImplantableDeviceForm from "../ImplantableDeviceForm/ImplantableDeviceForm.vue";

export default defineComponent({
    components: {
        FormSection,
        Pagination,
        SortableTh,
        ErrorMessageHandler,
        ImplantableDeviceRow,
        ImplantableDeviceForm
    },

    data() {
        return {
            _patientService: null,
            _toastrService: null,
            isAddRequested: false as boolean,
            selectedStatus: null as number,
            isAddInProgress: false as boolean,
            isLoading: false as boolean,
            page: null,
            sortBy: "udi" as string,
            sortOrder: "asc" as string,
            isError: false as boolean,
            columns: 4 as number,
            paginationInstance: null,
            deviceStatusEnum: Array.from(DeviceStatuses.enumWithDescriptions.values()) as IEnumResponse[],
            implantableDevice: new ImplantableDevice(),
            isDeviceValidated: false as boolean,
            newImplantableDeviceId: "" as string,
            implantableDeviceForm: null
        };
    },

    created() {
        this._patientService = Container.instance.get(PatientService);
        this._toastrService = Container.instance.get(ToastrService);
        this.paginationInstance = this.$refs.paginationInstance;
        this.implantableDeviceForm = this.$refs.implantableDeviceForm;
        this.selectedStatus = this.deviceStatusEnum.find((status: any) => status.name === "Active").value as number;
    },

    mounted() {
        this.paginationInstance = this.$refs.paginationInstance;
        this.implantableDeviceForm = this.$refs.implantableDeviceForm;
        this.resetList();
    },

    computed: {
        ...mapState(useConvertToPendingPal, ["accountId", "application", "patientId"]),
        loadingCount() {
            return !!this.page?.Items?.length ? this.page.Items.length : 3;
        },
        hasData() {
            return this.page?.ItemCount > 0;
        }
    },

    methods: {
        setStatus(status: number) {
            this.selectedStatus = status;
            this.resetList();
        },

        openAddImplantableDevice() {
            this.implantableDevice = new ImplantableDevice();
            this.isAddRequested = true;
        },

        async saveNewImplantableDevice(closeForm: boolean = true) {
            const isFormValid = await (this.$refs.implantableDeviceForm as any).v$.$validate();
            if (isFormValid) {
                try {
                    this.isAddInProgress = true;
                    const payload = {
                        Application: this.application,
                        AccountId: this.accountId,
                        PalliativePatientPayload: this.implantableDevice
                    };
                    let res = await this._patientService.createImplantableDevice(this.patientId, payload);
                    if (res.isSuccess) {
                        this._toastrService.success({
                            title: `Implantable Device Added`,
                            message: `The implantable device has been successfully added.`
                        });
                        if (closeForm) {
                            this.closeAddImplantableDevice();
                        }
                        this.implantableDevice = new ImplantableDevice();
                        this.resetList();
                    } else {
                        this._toastrService.error({
                            title: `Error - Add Implantable Device`,
                            message: `${res.message}`
                        });
                    }
                } catch (err) {
                    console.error(err);
                    this._toastrService.error({
                        title: `Error - Add Implantable Device`,
                        message: `There was a problem while adding the implantable device. Please try again.`
                    });
                } finally {
                    this.isAddInProgress = false;
                }
            } else {
                this._toastrService.error({
                    title: `Error - Implantable Device`,
                    message: "Please fill required fields"
                });
            }
        },

        closeAddImplantableDevice() {
            this.implantableDevice = new ImplantableDevice();
            this.isAddRequested = false;
            this.isDeviceValidated = false;
        },

        async pageChangedCallback(pageNumber: number, pageSize: number): Promise<IPageCallbackResult> {
            try {
                this.isLoading = true;
                let query: IGetImplantableDeviceQuery = {
                    page: pageNumber,
                    pageLength: pageSize,
                    sortBy: this.sortBy,
                    order: this.sortOrder,
                    deviceStatus: this.selectedStatus,
                    application: this.application,
                    accountId: this.accountId
                };
                const response = await this._patientService.getImplantableDevices(this.patientId, query);
                this.page = response.data;
                if (!this.page.Items) {
                    this.page.Items = [];
                }
                return {
                    totalPageCount: this.page.PageCount,
                    success: this.page.ItemCount > 0,
                    totalCount: this.page.ItemCount
                };
            } catch (e) {
                console.error(e);
                return PAGE_FAIL_RESPONSE;
            } finally {
                this.isLoading = false;
            }
        },
        resetList() {
            this.paginationInstance?.reset();
        },

        sortByUpdated(value: string) {
            if (value != this.sortBy) {
                this.sortBy = value;
                this.resetList();
            }
        },
        sortOrderUpdated(value: string) {
            if (value != this.sortOrder) {
                this.sortOrder = value;
                this.resetList();
            }
        }
    }
});
